import React from "react";
import { useTranslation } from "react-i18next";
import {
  addDays,
  eachDayOfInterval,
  format,
  isBefore,
  parseISO,
  startOfDay,
} from "date-fns";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartContainer,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import type { RoomUsageHistoryResponse } from "@/types/roomUsage";

interface RoomHistoryPerformanceCardProps {
  history?: RoomUsageHistoryResponse;
  isLoading?: boolean;
}

export function RoomHistoryPerformanceCard({
  history,
  isLoading = false,
}: RoomHistoryPerformanceCardProps) {
  const { t } = useTranslation();

  const chartConfig = React.useMemo(
    () =>
      ({
        nightsInRange: {
          label: t("reservation.roomUsage.nightsInRange"),
          color: "#C9A962",
        },
        nights: {
          label: t("reservation.roomUsage.nights"),
          color: "#8B7CF6",
        },
      }) satisfies ChartConfig,
    [t],
  );

  const chartData = React.useMemo(() => {
    if (!history?.from || !history?.to) {
      return [];
    }

    const rangeStart = startOfDay(parseISO(history.from));
    const rangeEnd = startOfDay(parseISO(history.to));
    const dayBuckets = eachDayOfInterval({
      start: rangeStart,
      end: rangeEnd,
    });

    const totalsByDay = new Map(
      dayBuckets.map((day) => [format(day, "yyyy-MM-dd"), 0]),
    );
    const guestsByDay = new Map<string, string[]>();

    for (const stay of history.stays) {
      const arrival = startOfDay(parseISO(stay.arrivalDate));
      const departure = startOfDay(parseISO(stay.departureDate));

      for (
        let cursor = arrival;
        isBefore(cursor, departure) ||
        format(cursor, "yyyy-MM-dd") === format(departure, "yyyy-MM-dd");
        cursor = addDays(cursor, 1)
      ) {
        const bucketKey = format(cursor, "yyyy-MM-dd");
        if (!totalsByDay.has(bucketKey)) {
          continue;
        }

        if (!isBefore(cursor, departure)) {
          break;
        }

        totalsByDay.set(bucketKey, (totalsByDay.get(bucketKey) ?? 0) + 1);
        guestsByDay.set(bucketKey, [
          ...(guestsByDay.get(bucketKey) ?? []),
          stay.guest.fullName,
        ]);
      }
    }

    return dayBuckets.map((day) => {
      const bucketKey = format(day, "yyyy-MM-dd");
      const guests = guestsByDay.get(bucketKey) ?? [];

      return {
        label: format(day, "d MMM"),
        fullDate: format(day, "d MMM yyyy"),
        guestsLabel: guests.join(", "),
        nights: totalsByDay.get(bucketKey) ?? 0,
        nightsInRange: totalsByDay.get(bucketKey) ?? 0,
      };
    });
  }, [history]);

  if (!chartData.length) {
    return (
      <div className="mt-6 rounded-2xl border border-border bg-background p-6">
        <div className="py-12 text-center text-sm text-muted-foreground">
          {isLoading
            ? t("reservation.roomUsage.loading")
            : t("reservation.roomUsage.historyEmpty")}
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 rounded-2xl border border-border bg-background p-6">
      <ChartContainer
        config={chartConfig}
        className="h-[260px] w-full !aspect-auto"
      >
        <ComposedChart
          data={chartData}
          margin={{ top: 12, right: 8, left: 8, bottom: 4 }}
        >
          <defs>
            <linearGradient id="room-range-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(201, 169, 98, 0.34)" />
              <stop offset="100%" stopColor="#C9A962" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="room-total-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(139, 124, 246, 0.28)" />
              <stop offset="100%" stopColor="#8B7CF6" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke="#F4F4F5" strokeWidth={1} />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fill: "#A3A3A3", fontSize: 12 }}
          />
          <YAxis hide />
          <Tooltip
            cursor={{ stroke: "#E4E4E7", strokeWidth: 1 }}
            content={
              <ChartTooltipContent
                labelFormatter={(_, payload) =>
                  payload?.[0]?.payload?.guestsLabel ||
                  payload?.[0]?.payload?.fullDate ||
                  ""
                }
              />
            }
          />
          <Area
            type="monotone"
            dataKey="nights"
            stroke="var(--color-nights)"
            fill="url(#room-total-fill)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}
          />
          <Area
            type="monotone"
            dataKey="nightsInRange"
            stroke="var(--color-nightsInRange)"
            fill="url(#room-range-fill)"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 5, strokeWidth: 2, stroke: "#fff" }}
          />
        </ComposedChart>
      </ChartContainer>
    </div>
  );
}
